// ---------------------------------------------------------------------------
// Dashboard.js — Morning Operations (Warm Stone Design)
// ---------------------------------------------------------------------------
// Reworked on the usability review of 23 October 2026 (items 9-15):
//
//   9   one row of key figures: NAV, the month's cash generated from deals,
//       cash, gross and net exposure, and the open items
//   10  an alerts strip under it: stale prices, unposted transactions,
//       notices waiting for review, upcoming deadlines
//   11-12  the positions table (DashboardPositions)
//   13  the Analytics tab's exposures and currency as small charts here
//   14  bank balances and treasury moved to the Bank Feed page
//   15  when the figures were last read, and a refresh every five minutes
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../api/client";
import DashboardPositions, { exposureSummary } from "../components/DashboardPositions";
import { PortfolioChanges, UpcomingCashEvents } from "../components/PortfolioInsights";
import { ConcentrationCard, CurrencyCard } from "../components/DashboardInsights";
import { portfolioResult, hasLiabilities } from "../components/portfolioDashboardData";
import MTDResultBreakdown from "../components/MTDResultBreakdown";
import MTDCashGenerated, { cashGeneratedSummary } from "../components/MTDCashGenerated";
import useCachedGet, { readCache, writeCache } from "../components/useCachedGet";
import { dashboardAlerts, openItemCount } from "../components/dashboardAlerts";
import { FreshnessBadge, PdfButton, InfoTip } from "../components/PageTools";
import { queryParam } from "../components/links";
import { money, pct, day, toNumber } from "../components/format";
import "./Dashboard.css";
import { Card, PageHeader, Button, LoadingSpinner } from "../components/ui";

const AUTO_REFRESH_MS = 5 * 60 * 1000;
const RESULTS_KEY = "dashboard:fund-results";

// The month's result for each fund book, read the way it always was: every
// entity, the funds among them, and the portfolio result of each. Cached like
// the other reads, so the figure is on screen before the server answers.
function useFundResults() {
  const [entry, setEntry] = useState(() => readCache(RESULTS_KEY));
  const [refreshing, setRefreshing] = useState(true);
  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      let url = "/core/entities/", entities = [];
      while (url) {
        const response = await api.get(url);
        entities = entities.concat(response.data.results || (Array.isArray(response.data) ? response.data : []));
        url = response.data.next || null;
      }
      const funds = entities.filter((e) => e.entity_type === "Fund" || ["HGIO", "HGIO1", "HSS1"].includes(e.short_name));
      const results = await Promise.all(funds.map(async (entity) => {
        const response = await api.get(`/reports/portfolio-result/?entity_id=${entity.id}`);
        return { ...response.data, bookName: entity.short_name || entity.name };
      }));
      setEntry(writeCache(RESULTS_KEY, results));
    } catch {
      setEntry((e) => e || { savedAt: null, data: [] });
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { data: entry?.data || [], savedAt: entry?.savedAt || null, refreshing, loading: !entry && refreshing, reload };
}

// The fund's NAV at the end of last month, from the recorded snapshots, so the
// month's result can be read as a percentage of what the month started with.
// None recorded within a few days of the month end means no percentage — a
// share of the wrong base is worse than none.
export function openingNav(navData) {
  const asOf = navData?.as_of_date;
  if (!asOf || !Array.isArray(navData.history)) return null;
  const monthStart = `${asOf.slice(0, 7)}-01`;
  const before = navData.history.filter((h) => h.date < monthStart).sort((a, b) => b.date.localeCompare(a.date))[0];
  if (!before) return null;
  const gapDays = (Date.parse(`${monthStart}T00:00:00Z`) - Date.parse(`${before.date}T00:00:00Z`)) / 86400000;
  if (gapDays > 5) return null;
  const value = toNumber(before.nav);
  return value ? { date: before.date, value } : null;
}

function Kpi({ label, value, sub, tone, tip, children }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}{tip && <InfoTip>{tip}</InfoTip>}</div>
      <div className={`kpi-value${tone ? ` kpi-${tone}` : ""}`}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
      {children}
    </div>
  );
}

function AlertsStrip({ alerts }) {
  const order = { warn: 0, info: 1, unknown: 2, ok: 3 };
  return (
    <section className="alerts-strip" aria-label="Needs attention">
      {[...alerts].sort((a, b) => order[a.tone] - order[b.tone]).map((a) => (
        <Link key={a.key} to={a.to} className={`alert-chip alert-${a.tone}`} title={a.detail}>
          <span className="alert-icon" aria-hidden="true">{a.tone === "ok" ? "✓" : a.tone === "unknown" ? "…" : a.tone === "info" ? "◷" : "!"}</span>
          <span className="alert-text">
            <span className="alert-label">{a.label}</span>
            <span className="alert-detail">{a.detail}</span>
          </span>
        </Link>
      ))}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
export default function Dashboard() {
  const location = useLocation();
  const initialQuery = queryParam(location.search);
  const navQ = useCachedGet("/market-data/nav/");
  const treasuryQ = useCachedGet("/reports/treasury/");
  const noticesQ = useCachedGet("/securities/notice-review/");
  const reviewQ = useCachedGet("/reports/needs-review/");
  const cashQ = useCachedGet("/reports/cash-generated/");
  const resultsQ = useFundResults();
  const [showResult, setShowResult] = useState(false);

  const reloadAll = useCallback(() => {
    navQ.reload(); treasuryQ.reload(); noticesQ.reload(); reviewQ.reload(); resultsQ.reload(); cashQ.reload();
  }, [navQ.reload, treasuryQ.reload, noticesQ.reload, reviewQ.reload, resultsQ.reload, cashQ.reload]); // eslint-disable-line react-hooks/exhaustive-deps

  // Item 15: "refresh automatically in the background". Every five minutes
  // while the page is in front of the reader; never from a hidden tab.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") reloadAll();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [reloadAll]);

  const navData = navQ.data;
  const reports = resultsQ.data;
  const summary = navData?.summary || {};
  const exposure = useMemo(() => exposureSummary(navData), [navData]);
  const alerts = useMemo(
    () => dashboardAlerts({ nav: navData, treasury: treasuryQ.data, notices: noticesQ.data, review: reviewQ.data }),
    [navData, treasuryQ.data, noticesQ.data, reviewQ.data],
  );

  if (navQ.loading && !navData) return <LoadingSpinner message="Loading dashboard..." />;

  const nav = toNumber(summary.nav);
  const result = portfolioResult(reports);
  const cash = cashGeneratedSummary(cashQ.data);
  const opening = openingNav(navData);
  const ofNav = (v) => (nav && v !== null ? `${pct((v / nav) * 100)} of NAV` : null);
  const savedAts = [navQ.savedAt, resultsQ.savedAt, cashQ.savedAt].filter(Boolean);
  const refreshing = navQ.refreshing || treasuryQ.refreshing || noticesQ.refreshing || reviewQ.refreshing || resultsQ.refreshing || cashQ.refreshing;
  const open = openItemCount(alerts);

  return (
    <div className="portfolio-dashboard">
      <PageHeader
        title="Portfolio dashboard"
        subtitle={`Helena Partners Fund Administration — ${new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`}
        action={
          <div className="dashboard-actions">
            <FreshnessBadge savedAt={savedAts.length ? Math.min(...savedAts) : null} refreshing={refreshing} error={navQ.error} />
            <Button onClick={reloadAll} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh All"}</Button>
            <PdfButton />
          </div>
        }
      />

      {navQ.error && !navData && <Card title="Fund NAV"><p>The valuation could not be loaded: {navQ.error}. Refresh to retry.</p></Card>}

      {/* ── One row of key figures (item 9) ── */}
      <div className="kpi-row">
        <Kpi
          label="Fund NAV"
          value={money(nav, { whole: true })}
          sub={navData?.as_of_date ? `As of ${day(navData.as_of_date)}` : "Date unavailable"}
          tip={<span className="portfolio-nav-bridge">{money(summary.total_positions, { whole: true })} positions + {money(summary.total_cash, { whole: true })} cash + {money(summary.total_accrued_interest, { whole: true })} accrued income{hasLiabilities(summary) && <> − {money(summary.total_liabilities, { whole: true })} liabilities</>} = {money(summary.nav, { whole: true })} NAV</span>}
        />
        {/* The month's cash from deals, not its P&L. Brian, 27 October 2026:
            "i guess what i really want to see instead of MTD pnl is MTD cash
            generated from deals...we cant eat unrealized". It is not a term of
            NAV either, so it is a button that opens its own breakdown, with
            the P&L and its unrealized movement beneath it. Cash is not profit
            ("we generated almost $50k in Live Oak but we lost money on a net
            basis", D-290), so the profit in it is beside it. */}
        <button type="button" className="kpi kpi-button" aria-expanded={showResult} aria-controls="mtd-result-detail" onClick={() => setShowResult(!showResult)}>
          <div className="kpi-label">Cash generated · MTD</div>
          <div className={`kpi-value${cash && cash.generated > 0 ? " kpi-pos" : ""}`}>
            {cashQ.loading ? "Loading…" : cash ? money(cash.generated, { whole: true }) : "Unavailable"}
          </div>
          <div className="kpi-sub">
            {cash ? `${cash.profit !== null ? `${cash.profit < 0 ? "Loss" : "Profit"} in it: ${money(cash.profit, { whole: true })} · ` : ""}Net of what went back in: ${money(cash.net, { whole: true })}` : "From deals, this month"}
            {" · "}{showResult ? "Hide breakdown ▴" : "Breakdown ▾"}
          </div>
        </button>
        <Kpi label="Cash" value={money(exposure.cash, { whole: true })} sub="Banks and prime brokers"
          tip="Cash in the fund's bank and broker accounts, in dollars, as NAV counts it. The per-account balances are on Bank Feed." />
        <Kpi label="Gross exposure" value={money(exposure.gross, { whole: true })} sub={ofNav(exposure.gross)}
          tip="Long positions plus the absolute value of short positions, at market value in dollars. Cash is not included." />
        <Kpi label="Net exposure" value={money(exposure.net, { whole: true })} sub={ofNav(exposure.net)}
          tip={`Long positions less short positions: ${money(exposure.long, { whole: true })} long, ${money(exposure.short, { whole: true })} short.`} />
        <Kpi label="Open items" value={open} tone={open ? "warn" : undefined} sub={open ? "Listed below" : "Nothing waiting"} />
      </div>

      <section id="mtd-result-detail" hidden={!showResult} aria-label="MTD cash generated breakdown">
        <MTDCashGenerated data={cashQ.data} loading={cashQ.loading} error={cashQ.error} />
        <details className="mtd-pnl">
          <summary>
            <span>MTD P&amp;L, marks included</span>
            <strong className={result < 0 ? "mtd-negative" : result > 0 ? "mtd-positive" : ""}>
              {resultsQ.loading ? "Loading…" : result === null ? "Incomplete" : money(result, { whole: true })}
            </strong>
            <small>
              {result === null
                ? "Opening marks need review"
                : opening ? `${pct((result / opening.value) * 100, 2)} of NAV at ${day(opening.date)}` : "No month-end NAV recorded"}
            </small>
          </summary>
          <MTDResultBreakdown reports={reports} loading={resultsQ.loading} />
          <PortfolioChanges reports={reports} loading={resultsQ.loading} />
        </details>
      </section>

      {/* ── What needs attention today (item 10) ── */}
      <AlertsStrip alerts={alerts} />

      {navData?.positions
        ? <DashboardPositions data={navData} initialQuery={initialQuery} />
        : <Card title="Fund Positions"><p>Positions could not be loaded. Refresh to retry.</p></Card>}

      {/* ── The Analytics tab, as small charts (item 13) ── */}
      <div className="portfolio-analytics">
        <ConcentrationCard nav={navData} />
        <CurrencyCard nav={navData} />
      </div>
      <UpcomingCashEvents data={navData} />
      <p className="dashboard-moved">Bank balances and treasury are on <Link to="/bank-feed">Bank Feed</Link>.</p>
    </div>
  );
}
